import { useState } from 'react';
import {
  accountFieldErrors, accountToForm, sectionHasChanges, type AccountField, type useAccountForm,
} from './useAccountForm';

interface SectionSaveState {
  saving: boolean;
  error: string | null;
  saved: boolean;
  /** Whether Save was pressed since the last success or discard: field errors show only then. */
  attempted: boolean;
}

const IDLE: SectionSaveState = { saving: false, error: null, saved: false, attempted: false };

/**
 * One tab's save: its fields, its own saving state and its own outcome, so a
 * save reports beside the fields it wrote rather than at the top of the dialog.
 */
export function useAccountSectionSave(form: ReturnType<typeof useAccountForm>, fields: readonly AccountField[]) {
  const [state, setState] = useState<SectionSaveState>(IDLE);
  const account = form.selectedEntity;
  const dirty = account ? sectionHasChanges(form.formData, account, fields) : false;
  const errors = state.attempted && account ? accountFieldErrors(form.formData, account) : {};

  const save = async () => {
    setState({ saving: true, error: null, saved: false, attempted: true });
    try {
      await form.saveFields(fields);
      setState({ ...IDLE, saved: true });
    } catch (cause) {
      setState({ saving: false, error: cause instanceof Error ? cause.message : String(cause), saved: false, attempted: true });
    }
  };

  const discard = () => {
    if (!account) return;
    const saved = accountToForm(account);
    fields.forEach((field) => form.handleChange(field, saved[field]));
    setState(IDLE);
  };

  return {
    dirty,
    saving: state.saving,
    error: state.error,
    // A later edit makes the last outcome stale, so it shows only while nothing moved.
    saved: state.saved && !dirty,
    /** A field's error, or null; always null until Save is pressed. */
    fieldError: (field: AccountField): string | null => errors[field] ?? null,
    save,
    discard,
    clearError: () => setState((s) => ({ ...s, error: null })),
  };
}
